import { useEffect, useState } from "react";
import API from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { SlackLogo, MicrosoftTeamsLogo, BellRinging, PaperPlaneTilt, Spinner, CheckCircle, XCircle } from "@phosphor-icons/react";

const EMPTY = { enabled: true, email_enabled: true, slack_webhook_url: "", teams_webhook_url: "", events: {}, error_cooldown_minutes: 60, recent: [], event_labels: {}, is_preview: false, email_policy: { alert_after_minutes: 15, cooldown_hours: 6 } };

function ago(iso) {
  if (!iso) return "";
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

// Platform-wide Slack / Teams channel for scheduler + deploy-health alerts (platform admins only).
export function OpsAlertChannelCard() {
  const [s, setS] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState("");
  const [forbidden, setForbidden] = useState(false);

  const load = () => API.get("/platform/ops-alerts")
    .then(({ data }) => setS({ ...EMPTY, ...data }))
    .catch((e) => { if (e?.response?.status === 403) setForbidden(true); })
    .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await API.put("/platform/ops-alerts", {
        enabled: s.enabled, email_enabled: s.email_enabled, slack_webhook_url: s.slack_webhook_url, teams_webhook_url: s.teams_webhook_url,
        events: s.events, error_cooldown_minutes: s.error_cooldown_minutes,
      });
      setS({ ...EMPTY, ...data });
      toast.success("Alert channel saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save alert channel");
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async (channel) => {
    setTesting(channel);
    try {
      const { data } = await API.post("/platform/ops-alerts/test", { channel });
      const failed = (data.results || []).filter((r) => !r.ok);
      if (failed.length) toast.warning(`Sent to ${data.sent}, failed: ${failed.map((r) => `${r.channel} (${r.error})`).join(", ")}`);
      else toast.success(`Test alert sent to ${data.results.map((r) => r.channel).join(" + ")}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test alert failed");
    } finally {
      setTesting("");
      load();
    }
  };

  if (forbidden) return null;
  const configured = !!(s.slack_webhook_url || s.teams_webhook_url);
  const events = Object.keys(s.event_labels || {});

  return (
    <Card data-testid="ops-alert-channel-card">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <BellRinging className="w-4 h-4" weight="duotone" /> Alerts to Slack / Teams
              {loading ? null : configured ? (
                <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300 dark:border-emerald-800" data-testid="ops-alert-status-badge">
                  {s.enabled ? "On" : "Paused"}
                </Badge>
              ) : (
                <Badge variant="outline" className="text-muted-foreground" data-testid="ops-alert-status-badge">Not connected</Badge>
              )}
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
              Ping an incoming webhook when a background job has been down for {s.email_policy?.alert_after_minutes ?? 15}+ minutes, raises an error or recovers — and when a deploy-health check fails.
            </p>
            <div className="mt-3 rounded-md border border-border bg-muted/30 px-3 py-2 max-w-2xl" data-testid="ops-alert-email-row">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">E-mail to platform admins (separate from Slack / Teams)</div>
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <Switch checked={s.email_enabled !== false} onCheckedChange={(v) => setS((x) => ({ ...x, email_enabled: v }))} data-testid="ops-alert-email-switch" />
                <span>
                  <span className="font-medium">{s.email_enabled !== false ? "E-mails on" : "E-mails off"}</span>
                  <span className="text-muted-foreground"> — only after a job has been down {s.email_policy?.alert_after_minutes ?? 15}+ min, at most one e-mail per job every {s.email_policy?.cooldown_hours ?? 6} h, plus one when it recovers.</span>
                </span>
              </label>
              {s.is_preview && <p className="mt-1 text-[11px] text-amber-700 dark:text-amber-400" data-testid="ops-alert-preview-note">This is the preview workspace — it never e-mails; only the published app does.</p>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 text-xs">
              <Switch checked={!!s.enabled} onCheckedChange={(v) => setS((x) => ({ ...x, enabled: v }))} data-testid="ops-alert-enabled-switch" />
              <span>{s.enabled ? "Alerts on" : "Alerts paused"}</span>
            </div>
            <Button size="sm" onClick={save} disabled={saving || loading} data-testid="ops-alert-save-btn">
              {saving ? <Spinner size={14} className="animate-spin mr-1" /> : null} Save
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div className="space-y-4">
            <div>
              <Label htmlFor="ops-slack" className="text-xs flex items-center gap-1.5"><SlackLogo size={14} weight="duotone" /> Slack incoming webhook</Label>
              <div className="flex gap-2 mt-1">
                <Input id="ops-slack" value={s.slack_webhook_url} onChange={(e) => setS((x) => ({ ...x, slack_webhook_url: e.target.value.trim() }))}
                  placeholder="https://hooks.slack.com/services/T000/B000/XXXX" className="font-mono text-xs" disabled={loading} data-testid="ops-alert-slack-url" />
                <Button variant="outline" size="sm" onClick={() => sendTest("slack")} disabled={!s.slack_webhook_url || !!testing} data-testid="ops-alert-test-slack-btn">
                  {testing === "slack" ? <Spinner size={14} className="animate-spin" /> : <PaperPlaneTilt size={14} />}
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">Slack → your app → Incoming Webhooks → Add New Webhook to Workspace.</p>
            </div>
            <div>
              <Label htmlFor="ops-teams" className="text-xs flex items-center gap-1.5"><MicrosoftTeamsLogo size={14} weight="duotone" /> Microsoft Teams incoming webhook</Label>
              <div className="flex gap-2 mt-1">
                <Input id="ops-teams" value={s.teams_webhook_url} onChange={(e) => setS((x) => ({ ...x, teams_webhook_url: e.target.value.trim() }))}
                  placeholder="https://….webhook.office.com/webhookb2/…" className="font-mono text-xs" disabled={loading} data-testid="ops-alert-teams-url" />
                <Button variant="outline" size="sm" onClick={() => sendTest("teams")} disabled={!s.teams_webhook_url || !!testing} data-testid="ops-alert-test-teams-btn">
                  {testing === "teams" ? <Spinner size={14} className="animate-spin" /> : <PaperPlaneTilt size={14} />}
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">Teams channel → ⋯ → Workflows → "Post to a channel when a webhook request is received" (or a legacy Incoming Webhook connector).</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-3 pt-1">
              {events.map((key) => (
                <label key={key} className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2 text-xs cursor-pointer" data-testid={`ops-alert-event-${key}`}>
                  <span>{s.event_labels[key]}</span>
                  <Switch checked={s.events?.[key] !== false} onCheckedChange={(v) => setS((x) => ({ ...x, events: { ...x.events, [key]: v } }))} data-testid={`ops-alert-event-switch-${key}`} />
                </label>
              ))}
            </div>
            <div className="flex items-center gap-3">
              <Label htmlFor="ops-cooldown" className="text-xs whitespace-nowrap">Repeat error pings at most every</Label>
              <Input id="ops-cooldown" type="number" min={0} max={1440} value={s.error_cooldown_minutes}
                onChange={(e) => setS((x) => ({ ...x, error_cooldown_minutes: e.target.value }))} className="w-24 h-8 text-xs" data-testid="ops-alert-cooldown-input" />
              <span className="text-xs text-muted-foreground">minutes per job</span>
            </div>
          </div>

          <div>
            <div className="text-xs font-semibold mb-2">Recent deliveries</div>
            {(s.recent || []).length === 0 ? (
              <p className="text-xs text-muted-foreground" data-testid="ops-alert-recent-empty">Nothing sent yet — save a webhook and press the send icon to test it.</p>
            ) : (
              <ul className="space-y-1.5 max-h-64 overflow-y-auto pr-1" data-testid="ops-alert-recent-list">
                {s.recent.map((r, i) => (
                  <li key={`${r.at}-${i}`} className="text-xs rounded-md border border-border px-2.5 py-1.5" data-testid="ops-alert-recent-row">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium truncate" title={r.title}>{r.title}</span>
                      <span className="text-muted-foreground whitespace-nowrap">{ago(r.at)}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 text-muted-foreground">
                      {(r.channels || []).map((c) => (
                        <span key={c.channel} className="inline-flex items-center gap-1" title={c.error || "delivered"}>
                          {c.ok ? <CheckCircle size={12} className="text-emerald-600" weight="fill" /> : <XCircle size={12} className="text-red-600" weight="fill" />}
                          {c.channel}
                        </span>
                      ))}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
